import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

//components
import AppHOC from "../../../../generics/AppHOC";

//utils
import {
  getLocalStorageFlag
} from "../../../../utils/common";
import usePredictiveNetworksHooks from "../../../insights/usePredictiveNetworkHooks";
import { validateProps } from "../../../../utils/waniCommon";
import { colors } from "../../../../utils/colors";

//style
import css from "../../insightsAppMagneticStyle.less";
import useI18n from "../../../../i18n/useI18n";

const [HbrCard, HbrIcon] = reactWrapper(["hbr-card", "hbr-icon"]);

const EMPTY_VALUE = "--";

const parseStoredDetails = key => {
  const value = getLocalStorageFlag(key);

  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getDisplayValue = value =>
  value === undefined || value === null || value === "" ? EMPTY_VALUE : value;

const getSummaryFromPathDetails = pathDetails => ({
  defaultQuality: pathDetails.defaultQuality,
  recommendationQuality: pathDetails.recommendationQuality,
  gains: pathDetails.gains,
  impactedUsers: pathDetails.impactedUsers ?? pathDetails.totalNumUsers
});

const getSummaryFromAction = action => ({
  defaultQuality: action.defaultQuality,
  recommendationQuality: action.recommendationQuality,
  gains: action.gains,
  impactedUsers: action.impactedUsers
});

const getNumericGain = gain => {
  if (gain === undefined || gain === null || gain === "") {
    return null;
  }

  const numericGain = Number(gain.toString().replace("%", "").trim());

  return Number.isFinite(numericGain) ? numericGain : null;
};

const getGainColor = gain => {
  if (gain > 0) {
    return colors.greenSuccess;
  }

  if (gain < 0) {
    return colors.redPoor;
  }

  return colors.gray15;
};

const getGainLabel = gain => {
  const gainString = gain.toString().replace("%", "").trim();

  return `${gainString.replace(/^\+/, "")}%`;
};

const renderGain = gain => {
  const numericGain = getNumericGain(gain);

  if (numericGain === null) {
    return null;
  }

  return (
    <span className="path-hero-card-gain hbr-type-body4">
      <HbrIcon
        name="trend-up"
        style={{ color: getGainColor(numericGain) }}
        aria-hidden="true"
      />
      <span>{getGainLabel(gain)}</span>
    </span>
  );
};

const HeroCards = props => {
  const { tViews } = useI18n('views');
  const { selectedOverlay, appGroup, onboardingStatus } = validateProps(
    props.globalFilter
  );
  const { state, getListActions } = usePredictiveNetworksHooks();
  const [summary, setSummary] = useState(
    getSummaryFromPathDetails(parseStoredDetails("pathDetails") || {})
  );

  useEffect(() => {
    const pathDetails = parseStoredDetails("pathDetails");

    if (pathDetails) {
      setSummary(getSummaryFromPathDetails(pathDetails));
      return;
    }

    if (onboardingStatus === "ACTIVE") {
      getListActions(selectedOverlay);
    }
  }, [appGroup, onboardingStatus, selectedOverlay]);

  useEffect(() => {
    if (!state.isFetchingActionsDone || !state.actionPaths) {
      return;
    }

    const actionCardSite = getLocalStorageFlag("actionCardSite");
    const actionId = getLocalStorageFlag("actionId");
    const actionsForSiteApp = state.actionPaths.filter(
      action =>
        action.siteId === actionCardSite?.toString() &&
        action.appClass === appGroup
    );
    const selectedAction =
      actionsForSiteApp.find(
        action => action.actionId?.toString() === actionId?.toString()
      ) ||
      actionsForSiteApp[0];

    if (selectedAction) {
      setSummary(getSummaryFromAction(selectedAction));
    }
  }, [state.isFetchingActionsDone, state.actionPaths, appGroup]);

  const cards = [
    {
      label: tViews('predictiveNetworks.currentPath'),
      value: getDisplayValue(summary.defaultQuality)
    },
    {
      label: tViews('predictiveNetworks.recomPathQuality'),
      value: getDisplayValue(summary.recommendationQuality),
      gain: summary.gains
    },
    {
      label: tViews('predictiveNetworks.impactedUsers'),
      value: getDisplayValue(summary.impactedUsers)
    }
  ];

  return (
    <div
      className={css["path-hero-cards"]}
      data-cy="path-hero-cards"
      data-testid="path-hero-cards"
    >
      {cards.map(card => (
        <HbrCard
          className="path-hero-card"
          key={card.label}
          data-testid="path-hero-card"
        >
          <div className="path-hero-card-label hbr-type-body3">
            {card.label}
          </div>
          <div className="path-hero-card-value hbr-type-h2">
            {card.value}
            {renderGain(card.gain)}
          </div>
        </HbrCard>
      ))}
    </div>
  );
};

HeroCards.propTypes = {
  globalFilter: PropTypes.object.isRequired
};

export default AppHOC(HeroCards);
